import { computed, markRaw } from 'vue';
import { createMachine, assign, fromPromise } from 'xstate';
import { useMachine } from '@xstate/vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import { usePayrollPeriods, PERIOD_STATUS } from './usePayrollPeriods';
import { usePayrollRulesEngine } from './usePayrollRulesEngine';
import { usePayrollCalculations } from './usePayrollCalculations';
import { useEmployeeAdvances } from './useEmployeeAdvances';
import { useEmployeeDeductions } from './useEmployeeDeductions';
import payrollRepository from '@/repositories/payroll.repository.ts';

// Step Components - imported for machine-driven rendering
import Step1PeriodConfig from '@/domains/payroll/wizard/Step1PeriodConfig.vue';
import Step2RulesPreview from '@/domains/payroll/wizard/Step2RulesPreview.vue';
import Step3RevenueConciliationSummary from '@/domains/payroll/wizard/Step3RevenueConciliationSummary.vue';
import Step5PaymentRulesConciliationSummary from '@/domains/payroll/wizard/Step5PaymentRulesConciliationSummary.vue';
// OLD: StepCashRecebimentos removed - unified into StepEmployeeAdvances
import StepEmployeeAdvances from '@/domains/payroll/wizard/StepEmployeeAdvances.vue';
import StepPayrollRefunds from '@/domains/payroll/wizard/StepPayrollRefunds.vue';
import StepEmployeeDeductions from '@/domains/payroll/wizard/StepEmployeeDeductions.vue';
import StepPayrollSummary from '@/domains/payroll/wizard/StepPayrollSummary.vue';
import StepFinish from '@/domains/payroll/wizard/StepFinish.vue';

// =============================================================================
// STEP COMPONENT MAPPING
// =============================================================================

/**
 * Maps step keys to their Vue components
 * Used by currentStepComponent computed to render the correct step
 * markRaw prevents Vue from making components reactive (performance)
 */
const STEP_COMPONENTS = {
  config: markRaw(Step1PeriodConfig),
  rules: markRaw(Step2RulesPreview),
  revenue: markRaw(Step3RevenueConciliationSummary),
  // OLD: advances removed - unified into employeeAdvances
  employeeAdvances: markRaw(StepEmployeeAdvances),
  refunds: markRaw(StepPayrollRefunds),
  employeeDeductions: markRaw(StepEmployeeDeductions),
  payment: markRaw(Step5PaymentRulesConciliationSummary),
  summary: markRaw(StepPayrollSummary),
  finish: markRaw(StepFinish)
};

/**
 * Maps machine states to step keys
 * Multiple states can map to the same step (e.g., creatingPeriod -> config)
 *
 * @param {string} state - Current machine state
 * @returns {string} Step key
 */
function getStepKeyFromState(state: any) {
  const stateToStep: any = {
    'idle': 'config',
    'config': 'config',
    'creatingPeriod': 'config',
    'loadingRules': 'rules',
    'resuming': 'config',
    'rules': 'rules',
    'revenue': 'revenue',
    // OLD: advances removed - unified into employeeAdvances
    'employeeAdvances': 'employeeAdvances',
    'refunds': 'refunds',
    'employeeDeductions': 'employeeDeductions',
    'payment': 'payment',
    'calculating': 'payment',
    'summary': 'summary',
    'confirming': 'summary',
    'finished': 'finish'
  };
  return stateToStep[state] || 'config';
}

// =============================================================================
// DATE NORMALIZATION HELPER
// =============================================================================

/**
 * Normalize a date value to ISO 8601 string format.
 * Handles Date objects, ISO strings, and null/undefined values.
 *
 * This is critical because:
 * - CREATE flow: useEmployeePayroll converts dates to Date objects
 * - EDIT flow: usePayrollPeriods keeps dates as ISO strings
 * - XState: Date objects may become null during event processing
 *
 * By normalizing to ISO strings, we ensure consistent behavior in both flows.
 *
 * @param {Date|string|null} value - Date object or ISO string
 * @returns {string|null} - ISO 8601 string or null
 */
function normalizeDateToISO(value: any) {
  if (!value) return null;

  // Already a string - return as-is
  if (typeof value === 'string') {
    return value;
  }

  // Date object - convert to ISO string
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value.toISOString();
  }

  // Unknown type - log warning and return null
  log('usePayrollWizardMachine', 'normalizeDateToISO', 'WARNING: Unknown date type', {
    value,
    type: typeof value
  });
  return null;
}

/**
 * XState Machine for Payroll Wizard
 *
 * This is the SINGLE SOURCE OF TRUTH for:
 * - Wizard step flow (states)
 * - Allowed transitions (events + guards)
 * - Period/rules/calculation data (context)
 * - Async operations (services)
 *
 * Benefits over manual state management:
 * - Explicit state transitions (no impossible states)
 * - Guards prevent invalid navigation
 * - Async operations handled with proper error states
 * - Easy to visualize and debug
 * - Edit mode (resuming) handled explicitly
 *
 * @see https://stately.ai/docs/machines
 */

// =============================================================================
// MACHINE DEFINITION
// =============================================================================

/**
 * Initial context for the payroll wizard
 * Contains all data needed across wizard steps
 */
const initialContext: any = {
  // Period data
  period: null,           // Active period (created or loaded from edit)
  periodFormData: {       // Form inputs for period creation (employee-centered)
    franchise_id: null,
    user_id: null,        // Employee Cognito sub (replaces frequency in employee-centered architecture)
    start_date: null,
    end_date: null
  },
  isEditMode: false,      // True if resuming existing period

  // Rules data
  rules: [] as any[],              // Payment rules for the period

  // Calculation data
  calculationData: {
    period_id: null,
    gross_revenue: 0,
    total_expenses: 0,
    revenue_conciliation_status: 'pending',
    expenses_conciliation_status: 'pending'
  },
  calculationSummary: {
    executed: 0,
    soldRemotely: 0,
    fixed: 0,
    total: 0
  },
  calculationsComplete: false,

  // User data
  paymentByUser: [],      // Users with calculated amounts

  // Refunds
  pendingRefundsCount: 0,

  // Employee Advances (NEW)
  advanceDiscounts: [],   // Array of { advance_id, amount }
  advanceDiscountsTotal: 0, // Total to discount from advances

  // Employee Deductions (NEW)
  deductionDiscounts: [], // Array of { deduction_id, amount }
  deductionDiscountsTotal: 0, // Total to discount from deductions

  // Confirmation result
  confirmationResult: {
    success: false,
    error: null,
    despesas: []
  },

  // Error tracking
  error: null,

  // =========================================================================
  // PHASE 2: CONSOLIDATED DATA STATE (Single Source of Truth)
  // =========================================================================
  // This structure consolidates ALL wizard data in one place.
  // Components should read from here via inject('wizardMachine'),
  // NOT maintain local state.
  //
  // Data Loading Flow:
  // 1. Enter employeeAdvances state → LOAD_ADVANCES_DATA service
  // 2. Enter employeeDeductions state → LOAD_DEDUCTIONS_DATA service
  // 3. Enter refunds state → LOAD_REFUNDS_DATA service
  //
  // Data Update Flow:
  // 1. User selects items in component
  // 2. Component emits UPDATE_X_DISCOUNTS event
  // 3. Machine assigns to context.data.X.selected
  // 4. Component re-renders from updated context
  // =========================================================================

  data: {
    // Employee Advances Data
    advances: {
      // Available open/partial advances (from repository)
      available: [],
      // Selected advances for discounting (updated by UPDATE_ADVANCE_DISCOUNTS)
      selected: [],
      // Total amount to discount (computed from selected)
      total: 0,
      // Available cash recebimentos for creating new advances
      cash_recebimentos: [],
      // Summary grouped by employee (for display)
      summary_by_employee: []
    },

    // Employee Deductions Data
    deductions: {
      // Available open/partial deductions (from repository)
      available: [],
      // Selected deductions for discounting (updated by UPDATE_DEDUCTION_DISCOUNTS)
      selected: [],
      // Total amount to discount (computed from selected)
      total: 0,
      // Summary grouped by employee (for display)
      summary_by_employee: []
    },

    // Refunds Data
    refunds: {
      // Available pending refunds (from repository)
      available: [],
      // Refunds that need processing (same as available initially)
      pending: [],
      // Decisions made by user (updated per refund)
      decisions: [],
      // Total accepted amount (computed from decisions)
      total_accepted: 0,
      // Summary grouped by user (for display)
      summary_by_user: []
    }
  },

  // State flags for tracking wizard state
  flags: {
    // Has advances data been loaded from repository
    advances_loaded: false,
    // Has deductions data been loaded from repository
    deductions_loaded: false,
    // Has refunds data been loaded from repository
    refunds_loaded: false,
    // Has any changes been made (for dirty checking)
    has_changes: false
  },

  // UI state
  ui: {
    // Current step index (in visibleSteps array)
    current_step: 0,
    // Is wizard modal visible
    is_visible: false
  }
};;

/**
 * Create the payroll wizard machine definition
 *
 * @returns {StateMachine} XState machine definition
 */
export const createPayrollWizardMachine = () => {
  return createMachine({
    id: 'payrollWizard',
    initial: 'idle',
    context: initialContext,

    // Global events - handled from ANY state
    on: {
      RESET: {
        target: '.idle',
        actions: 'resetContext'
      },
      // GO_TO_STEP allows direct navigation to any step state
      // Used by StateMachineWizardModal when allowStepClick is enabled
      GO_TO_STEP: [
        { target: '.config', guard: ({ event }) => event.stepKey === 'config' },
        { target: '.rules', guard: ({ event }) => event.stepKey === 'rules' },
        { target: '.revenue', guard: ({ event }) => event.stepKey === 'revenue' },
        // OLD: advances removed - unified into employeeAdvances
        { target: '.employeeAdvances', guard: ({ event }) => event.stepKey === 'employeeAdvances' },
        { target: '.refunds', guard: ({ event }) => event.stepKey === 'refunds' },
        { target: '.employeeDeductions', guard: ({ event }) => event.stepKey === 'employeeDeductions' },
        { target: '.payment', guard: ({ event }) => event.stepKey === 'payment' },
        { target: '.summary', guard: ({ event }) => event.stepKey === 'summary' },
        { target: '.finished', guard: ({ event }) => event.stepKey === 'finish' }
      ]
    },

    states: {
      // =======================================================================
      // ENTRY POINT
      // =======================================================================

      /**
       * Idle state - waiting for wizard to open
       * Determines whether to start new period or resume existing
       */
      idle: {
        on: {
          START_NEW: {
            target: 'config',
            actions: 'resetContext'
          },
          RESUME_PERIOD: {
            target: 'resuming',
            actions: 'assignExistingPeriod'
          }
        }
      },

      // =======================================================================
      // EDIT MODE ENTRY
      // =======================================================================

      /**
       * Resuming state - loading existing period data
       * Navigates to correct step based on period status
       */
      resuming: {
        invoke: {
          id: 'loadPeriodData',
          src: 'loadPeriodData',
          input: ({ context }) => ({ context }),
          onDone: [
            // Navigate based on period status (most advanced first)
            // IMPORTANT: All transitions must run assignLoadedData to populate context
            { target: 'finished', guard: 'isPaid', actions: 'assignLoadedData' },
            { target: 'summary', guard: 'isCalculated', actions: 'assignLoadedData' },
            { target: 'payment', guard: 'hasCalculationsComplete', actions: 'assignLoadedData' },
            { target: 'rules', actions: 'assignLoadedData' }  // Default: start from rules
          ],
          onError: {
            target: 'idle',
            actions: 'assignError'
          }
        }
      },

      // =======================================================================
      // STEP 1: PERIOD CONFIGURATION
      // =======================================================================

      /**
       * Config state - user configures period (franchise, frequency, dates)
       */
      config: {
        on: {
          UPDATE_FORM: {
            actions: 'updateFormData'
          },
          NEXT: [
            // In edit mode, skip period creation and go to loading rules
            {
              target: 'loadingRules',
              guard: 'isEditMode'
            },
            // In new mode, create the period first
            {
              target: 'creatingPeriod',
              guard: 'isConfigValid'
            }
          ],
          SUBMIT: {
            target: 'creatingPeriod',
            guard: 'isConfigValid'
          },
          CANCEL: 'idle'
        }
      },

      /**
       * Creating period in backend
       */
      creatingPeriod: {
        invoke: {
          id: 'createPeriod',
          src: 'createPeriod',
          input: ({ context }) => ({ context }),
          onDone: {
            target: 'loadingRules',
            actions: 'assignPeriod'
          },
          onError: {
            target: 'config',
            actions: 'assignError'
          }
        }
      },

      /**
       * Loading rules after period creation
       */
      loadingRules: {
        invoke: {
          id: 'loadRules',
          src: 'loadRules',
          input: ({ context }) => ({ context }),
          onDone: {
            target: 'rules',
            actions: 'assignRules'
          },
          onError: {
            target: 'config',
            actions: 'assignError'
          }
        }
      },

      // =======================================================================
      // STEP 2: RULES PREVIEW
      // =======================================================================

      /**
       * Rules state - user reviews payment rules
       */
      rules: {
        on: {
          NEXT: [
            { target: 'revenue', guard: 'hasGrossRevenueRules' },
            // OLD: advances removed - unified into employeeAdvances
            { target: 'employeeAdvances' }
          ],
          BACK: { target: 'config', guard: 'isNewPeriod' }
        }
      },

      // =======================================================================
      // STEP 3: REVENUE CONCILIATION
      // =======================================================================

      /**
       * Revenue state - user conciliates revenue (recebimentos)
       * Only shown if hasGrossRevenueRules
       */
      revenue: {
        on: {
          UPDATE_REVENUE: {
            actions: 'updateRevenueData'
          },
          NEXT: 'employeeAdvances',
          BACK: 'rules'
        }
      },

      // =======================================================================
      // STEP 4: EMPLOYEE ADVANCES (NEW)
      // OLD: advances state removed - unified into employeeAdvances
      // =======================================================================

      /**
       * Employee Advances state - user selects open/partial employee advances to discount
       * This is the NEW employee advances system (independent from payroll)
       *
       * PHASE 2: Now uses loading state for data fetching
       */
      employeeAdvances: {
        initial: 'loading',
        states: {
          /**
           * PHASE 2: Loading advances data from repository
           * Uses consolidated data structure
           */
          loading: {
            invoke: {
              id: 'loadAdvancesData',
              src: 'loadAdvancesData',
              input: ({ context }) => ({ context }),
              onDone: {
                target: 'interactive',
                actions: 'assignAdvancesData'
              },
              onError: {
                target: 'error',
                actions: 'assignAdvancesError'
              }
            }
          },

          /**
           * Interactive state - user can modify advance selections
           * PHASE 3: Added UPDATE_ADVANCE_SELECTIONS for wizard-driven updates
           */
          interactive: {
            on: {
              /**
               * Legacy event: Discounts array format
               * Used by existing components during migration
               */
              UPDATE_ADVANCE_DISCOUNTS: {
                actions: 'assignAdvanceDiscounts'
              },
              /**
               * PHASE 3: New event - Selections object format
               * Used by refactored StepEmployeeAdvances
               * Converts selections { advance_id: amount } to discounts array
               */
              UPDATE_ADVANCE_SELECTIONS: {
                actions: 'assignAdvanceSelections'
              },
              /**
               * PHASE 3: Reload advances data
               * Transitions back to loading state
               */
              RELOAD_ADVANCES: 'loading',
              NEXT: [
                { target: '#payrollWizard.refunds', guard: 'hasPendingRefunds' },
                { target: '#payrollWizard.employeeDeductions', guard: 'areAdvanceDiscountsValid' }
              ],
              BACK: [
                // OLD: advances removed - now go back to revenue or rules
                { target: '#payrollWizard.revenue', guard: 'hasGrossRevenueRules' },
                { target: '#payrollWizard.rules' }
              ]
            }
          },

          /**
           * Error state - show error and allow retry or skip
           * PHASE 3: Added RETRY_ADVANCES alias for component consistency
           */
          error: {
            on: {
              RETRY: 'loading',
              RETRY_ADVANCES: 'loading',  // PHASE 3: Alias for component consistency
              SKIP: [
                { target: '#payrollWizard.refunds', guard: 'hasPendingRefunds' },
                { target: '#payrollWizard.employeeDeductions' }
              ]
            }
          }
        }
      },

      // =======================================================================
      // STEP 5: REFUNDS
      // =======================================================================

      /**
       * Refunds state - user reviews pending refund requests
       * Only shown if hasPendingRefunds > 0
       *
       * PHASE 2: Now uses loading state for data fetching
       */
      refunds: {
        initial: 'loading',
        states: {
          /**
           * PHASE 2: Loading refunds data from repository
           * Uses consolidated data structure
           */
          loading: {
            invoke: {
              id: 'loadRefundsData',
              src: 'loadRefundsData',
              input: ({ context }) => ({ context }),
              onDone: {
                target: 'interactive',
                actions: 'assignRefundsData'
              },
              onError: {
                target: 'error',
                actions: 'assignRefundsError'
              }
            }
          },

          /**
           * Interactive state - user can make refund decisions
           * PHASE 3: Added RELOAD_REFUNDS for wizard-driven updates
           */
          interactive: {
            on: {
              /**
               * PHASE 3: Reload refunds data
               * Transitions back to loading state
               */
              RELOAD_REFUNDS: 'loading',
              NEXT: '#payrollWizard.employeeDeductions',
              BACK: '#payrollWizard.employeeAdvances'
            }
          },

          /**
           * Error state - show error and allow retry or skip
           * PHASE 3: Added RETRY_REFUNDS alias for component consistency
           */
          error: {
            on: {
              RETRY: 'loading',
              RETRY_REFUNDS: 'loading',  // PHASE 3: Alias for component consistency
              SKIP: '#payrollWizard.employeeDeductions'
            }
          }
        }
      },

      // =======================================================================
      // STEP 5: EMPLOYEE DEDUCTIONS (NEW)
      // =======================================================================

      /**
       * Employee Deductions state - user selects open/partial employee deductions to discount
       * This is the NEW employee deductions system (independent from payroll)
       *
       * PHASE 2: Now uses loading state for data fetching
       */
      employeeDeductions: {
        initial: 'loading',
        states: {
          /**
           * PHASE 2: Loading deductions data from repository
           * Uses consolidated data structure
           */
          loading: {
            invoke: {
              id: 'loadDeductionsData',
              src: 'loadDeductionsData',
              input: ({ context }) => ({ context }),
              onDone: {
                target: 'interactive',
                actions: 'assignDeductionsData'
              },
              onError: {
                target: 'error',
                actions: 'assignDeductionsError'
              }
            }
          },

          /**
           * Interactive state - user can modify deduction selections
           * PHASE 3: Added UPDATE_DEDUCTION_SELECTIONS for wizard-driven updates
           */
          interactive: {
            on: {
              /**
               * Legacy event: Discounts array format
               * Used by existing components during migration
               */
              UPDATE_DEDUCTION_DISCOUNTS: {
                actions: 'assignDeductionDiscounts'
              },
              /**
               * PHASE 3: New event - Selections object format
               * Used by refactored StepEmployeeDeductions
               * Converts selections { deduction_id: amount } to discounts array
               */
              UPDATE_DEDUCTION_SELECTIONS: {
                actions: 'assignDeductionSelections'
              },
              /**
               * PHASE 3: Reload deductions data
               * Transitions back to loading state
               */
              RELOAD_DEDUCTIONS: 'loading',
              NEXT: { target: '#payrollWizard.payment', guard: 'areDeductionDiscountsValid' },
              BACK: [
                { target: '#payrollWizard.refunds', guard: 'hasPendingRefunds' },
                { target: '#payrollWizard.employeeAdvances' }
              ]
            }
          },

          /**
           * Error state - show error and allow retry or skip
           * PHASE 3: Added RETRY_DEDUCTIONS alias for component consistency
           */
          error: {
            on: {
              RETRY: 'loading',
              RETRY_DEDUCTIONS: 'loading',  // PHASE 3: Alias for component consistency
              SKIP: '#payrollWizard.payment'
            }
          }
        }
      },

      // =======================================================================
      // STEP 6: PAYMENT CALCULATIONS (User Review)
      // =======================================================================

      /**
       * Payment state - user reviews and triggers calculations
       * This is NOT an invoke state - user must click "Start Calculations"
       *
       * Flow options:
       * 1. User clicks "Iniciar Cálculos" button → opens PayrollCalculationsDialog
       *    → Dialog runs calculations → Dialog emits event → CALCULATIONS_DONE → summary
       * 2. User clicks "Próximo" after calculations done → summary (via guard)
       * 3. User clicks "Próximo" before calculations → calculating (machine runs them)
       */
      payment: {
        on: {
          START_CALCULATIONS: 'calculating',
          CALCULATIONS_DONE: {
            target: 'summary',
            actions: 'assignCalculationsFromDialog'
          },
          NEXT: [
            { target: 'summary', guard: 'hasCalculationsComplete' },
            { target: 'calculating' }
          ],
          BACK: 'employeeDeductions'
        }
      },

      // =======================================================================
      // STEP 6b: CALCULATING (Auto - runs calculations)
      // =======================================================================

      /**
       * Calculating state - running payment calculations
       * This invoke state is triggered by START_CALCULATIONS from payment state
       */
      calculating: {
        invoke: {
          id: 'runCalculations',
          src: 'runCalculations',
          input: ({ context }) => ({ context }),
          onDone: {
            target: 'summary',
            actions: 'assignCalculations'
          },
          onError: {
            target: 'payment',
            actions: 'assignError'
          }
        }
      },

      // =======================================================================
      // STEP 7: SUMMARY
      // =======================================================================

      /**
       * Summary state - user reviews calculation results
       */
      summary: {
        on: {
          CONFIRM: 'confirming',
          BACK: 'payment'
        }
      },

      // =======================================================================
      // STEP 8: CONFIRMING
      // =======================================================================

      /**
       * Confirming state - creating despesas in backend
       */
      confirming: {
        invoke: {
          id: 'confirmPeriod',
          src: 'confirmPeriod',
          input: ({ context }) => ({ context }),
          onDone: {
            target: 'finished',
            actions: 'assignConfirmation'
          },
          onError: {
            target: 'summary',
            actions: 'assignError'
          }
        }
      },

      // =======================================================================
      // STEP 9: FINISHED
      // =======================================================================

      /**
       * Finished state - wizard complete, show results
       */
      finished: {
        type: 'final',
        entry: 'emitSaved'
      }
    }
  }, {
    // =========================================================================
    // GUARDS
    // =========================================================================
    guards: {
      /**
       * Check if config form is valid
       */
      isConfigValid: ({ context }) => {
        const { periodFormData } = context;
        return !!(
          periodFormData.franchise_id &&
          periodFormData.user_id &&  // Employee-centered: user_id instead of frequency
          periodFormData.start_date &&
          periodFormData.end_date
        );
      },

      /**
       * Check if this is a new period (not edit mode)
       */
      isNewPeriod: ({ context }) => !context.isEditMode,

      /**
       * Check if this is edit mode (resuming existing period)
       */
      isEditMode: ({ context }) => context.isEditMode,

      /**
       * Check if there are gross revenue commission rules
       */
      hasGrossRevenueRules: ({ context }) => {
        return context.rules.some((rule: any) =>
          rule.rule_type?.startsWith('commission_over_gross_revenue')
        );
      },

      /**
       * Check if there are commission over execution rules (for advances step)
       */
      hasCommissionRules: ({ context }) => {
        return context.rules.some((rule: any) =>
          rule.rule_type === 'commission_over_gross_revenue_executed'
        );
      },

      /**
       * Check if there are pending refunds
       */
      hasPendingRefunds: ({ context }) => {
        return context.pendingRefundsCount > 0;
      },

      /**
       * Check if calculations are complete
       */
      hasCalculationsComplete: ({ context }) => {
        return context.calculationsComplete;
      },

      /**
       * Check if period is already calculated (status >= CALCULATED)
       */
      isCalculated: ({ context }) => {
        const status = parseInt((context.period as any)?.status);
        return !isNaN(status) && status >= PERIOD_STATUS.CALCULATED;
      },

      /**
       * Check if period is already paid/finished
       */
      isPaid: ({ context }) => {
        const status = parseInt((context.period as any)?.status);
        return !isNaN(status) && status >= PERIOD_STATUS.PAID;
      },

      /**
       * Check if advance discounts are valid (no amount exceeds remaining)
       * PHASE 2: Uses consolidated data structure (context.data.advances.available)
       */
      areAdvanceDiscountsValid: ({ context }) => {
        // Check against both legacy and new structure during migration
        const discounts = context.advanceDiscounts || context.data?.advances?.selected || [];
        const hasOldFormat = (context.advanceDiscounts || []).length > 0;
        const hasNewFormat = (context.data?.advances?.selected || []).length > 0;

        if (discounts.length === 0) {
          // PAYROLL_REFACTOR logging for guard validation
          log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'GUARD_CHECK', {
            guard: 'areAdvanceDiscountsValid',
            result: true,
            reason: 'no_discounts',
            oldFormat: { has: hasOldFormat, count: context.advanceDiscounts?.length || 0 },
            newFormat: { has: hasNewFormat, count: context.data?.advances?.selected?.length || 0 },
            allowsTransition: 'YES'
          });
          return true; // No advances selected, valid
        }

        // Get available advances from consolidated data structure
        const advances = context.data?.advances?.available || [];

        for (const discount of discounts) {
          const advance = advances.find((a: any) => String(a.id) === String((discount as any).advance_id));
          if (advance && (discount as any).amount > (advance as any).amount_remaining) {
            // PAYROLL_REFACTOR logging for guard validation failure
            log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'GUARD_CHECK', {
              guard: 'areAdvanceDiscountsValid',
              result: false,
              reason: 'amount_exceeds_remaining',
              advanceId: (discount as any).advance_id,
              discountAmount: (discount as any).amount,
              remainingAmount: (advance as any).amount_remaining,
              oldFormat: { has: hasOldFormat, count: context.advanceDiscounts?.length || 0 },
              newFormat: { has: hasNewFormat, count: context.data?.advances?.selected?.length || 0 },
              allowsTransition: 'NO'
            });
            return false; // Invalid: discount exceeds remaining
          }
        }

        // PAYROLL_REFACTOR logging for guard validation success
        log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'GUARD_CHECK', {
          guard: 'areAdvanceDiscountsValid',
          result: true,
          reason: 'all_discounts_valid',
          discountCount: discounts.length,
          oldFormat: { has: hasOldFormat, count: context.advanceDiscounts?.length || 0 },
          newFormat: { has: hasNewFormat, count: context.data?.advances?.selected?.length || 0 },
          allowsTransition: 'YES'
        });

        return true; // All discounts are valid
      },

      /**
       * Check if deduction discounts are valid (no amount exceeds remaining)
       * PHASE 2: Uses consolidated data structure (context.data.deductions.available)
       */
      areDeductionDiscountsValid: ({ context }) => {
        // Check against both legacy and new structure during migration
        const discounts = context.deductionDiscounts || context.data?.deductions?.selected || [];
        const hasOldFormat = (context.deductionDiscounts || []).length > 0;
        const hasNewFormat = (context.data?.deductions?.selected || []).length > 0;

        if (discounts.length === 0) {
          // PAYROLL_REFACTOR logging for guard validation
          log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'GUARD_CHECK', {
            guard: 'areDeductionDiscountsValid',
            result: true,
            reason: 'no_discounts',
            oldFormat: { has: hasOldFormat, count: context.deductionDiscounts?.length || 0 },
            newFormat: { has: hasNewFormat, count: context.data?.deductions?.selected?.length || 0 },
            allowsTransition: 'YES'
          });
          return true; // No deductions selected, valid
        }

        // Get available deductions from consolidated data structure
        const deductions = context.data?.deductions?.available || [];

        for (const discount of discounts) {
          const deduction = deductions.find((d: any) => String(d.id) === String((discount as any).deduction_id));
          if (deduction && (discount as any).amount > (deduction as any).amount_remaining) {
            // PAYROLL_REFACTOR logging for guard validation failure
            log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'GUARD_CHECK', {
              guard: 'areDeductionDiscountsValid',
              result: false,
              reason: 'amount_exceeds_remaining',
              deductionId: (discount as any).deduction_id,
              discountAmount: (discount as any).amount,
              remainingAmount: (deduction as any).amount_remaining,
              oldFormat: { has: hasOldFormat, count: context.deductionDiscounts?.length || 0 },
              newFormat: { has: hasNewFormat, count: context.data?.deductions?.selected?.length || 0 },
              allowsTransition: 'NO'
            });
            return false; // Invalid: discount exceeds remaining
          }
        }

        // PAYROLL_REFACTOR logging for guard validation success
        log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'GUARD_CHECK', {
          guard: 'areDeductionDiscountsValid',
          result: true,
          reason: 'all_discounts_valid',
          discountCount: discounts.length,
          oldFormat: { has: hasOldFormat, count: context.deductionDiscounts?.length || 0 },
          newFormat: { has: hasNewFormat, count: context.data?.deductions?.selected?.length || 0 },
          allowsTransition: 'YES'
        });

        return true; // All discounts are valid
      }
    },

    // =========================================================================
    // ACTIONS
    // =========================================================================
    actions: {
      /**
       * Reset context to initial state
       */
      resetContext: assign(() => ({
        ...initialContext,
        periodFormData: { ...initialContext.periodFormData }
      })),

      /**
       * Assign existing period for edit mode
       */
      assignExistingPeriod: assign({
        period: ({ event, context }: any) => {
          const apiPeriod = event.period || {};
          const formData = context.periodFormData || {};

          // Normalize dates to ISO strings (handles both Date objects and strings)
          // CREATE flow passes Date objects, EDIT flow passes ISO strings
          const startDate = normalizeDateToISO(apiPeriod.start_date) ||
                           normalizeDateToISO(apiPeriod.period_start_date) ||
                           normalizeDateToISO(formData.start_date);
          const endDate = normalizeDateToISO(apiPeriod.end_date) ||
                         normalizeDateToISO(apiPeriod.period_end_date) ||
                         normalizeDateToISO(formData.end_date);

          log('usePayrollWizardMachine', 'assignExistingPeriod', 'Assigning existing period with normalized dates', {
            'raw.apiPeriod.start_date': apiPeriod.start_date,
            'raw.apiPeriod.period_start_date': apiPeriod.period_start_date,
            'raw.formData.start_date': formData.start_date,
            'normalized.start_date': startDate,
            'normalized.end_date': endDate
          });

          // Merge API period with normalized dates
          return {
            ...apiPeriod,
            // SINGLE SOURCE OF TRUTH: start_date and end_date as ISO strings
            start_date: startDate,
            end_date: endDate,
            // Also set period_start_date/period_end_date for backward compatibility
            period_start_date: startDate,
            period_end_date: endDate
          };
        },
        isEditMode: true,
        periodFormData: (({ event, context }: any) => {
          const apiPeriod = event.period || {};
          const formData = context.periodFormData || {};

          // Normalize dates to ISO strings for periodFormData as well
          const startDate = normalizeDateToISO(apiPeriod.period_start_date) ||
                           normalizeDateToISO(apiPeriod.start_date) ||
                           normalizeDateToISO(formData.start_date);
          const endDate = normalizeDateToISO(apiPeriod.period_end_date) ||
                         normalizeDateToISO(apiPeriod.end_date) ||
                         normalizeDateToISO(formData.end_date);

          return {
            franchise_id: Number(apiPeriod.franchise_id),
            user_id: apiPeriod.user_id,  // Employee-centered: user_id instead of frequency
            // Normalized dates as ISO strings
            start_date: startDate,
            end_date: endDate
          };
        }) as any,
        calculationData: (({ event }: any) => ({
          ...initialContext.calculationData,
          period_id: Number(event.period?.id)
        })) as any
      }),

      /**
       * Update form data during config step
       */
      updateFormData: assign({
        periodFormData: ({ context, event }: any) => ({
          ...context.periodFormData,
          ...event.data
        })
      }),

      /**
       * Assign created period from API
       * Also switches to edit mode to prevent creating duplicate periods
       * Preserves dates from periodFormData in case API response doesn't include them
       */
      assignPeriod: assign({
        period: ({ context, event }: any) => {
          const apiPeriod = event.output || {};
          const formData = context.periodFormData || {};

          // Normalize dates to ISO strings (consistent with assignExistingPeriod)
          const startDate = normalizeDateToISO(apiPeriod.start_date) ||
                           normalizeDateToISO(apiPeriod.period_start_date) ||
                           normalizeDateToISO(formData.start_date);
          const endDate = normalizeDateToISO(apiPeriod.end_date) ||
                         normalizeDateToISO(apiPeriod.period_end_date) ||
                         normalizeDateToISO(formData.end_date);

          log('usePayrollWizardMachine', 'assignPeriod', 'Assigning created period with normalized dates', {
            'raw.apiPeriod.start_date': apiPeriod.start_date,
            'raw.apiPeriod.period_start_date': apiPeriod.period_start_date,
            'raw.formData.start_date': formData.start_date,
            'normalized.start_date': startDate,
            'normalized.end_date': endDate
          });

          // Merge API period with normalized dates
          return {
            ...apiPeriod,
            // SINGLE SOURCE OF TRUTH: start_date and end_date as ISO strings
            start_date: startDate,
            end_date: endDate,
            // Also set period_start_date/period_end_date for backward compatibility
            period_start_date: startDate,
            period_end_date: endDate
          };
        },
        isEditMode: true,  // Switch to edit mode after period creation
        calculationData: (({ context, event }: any) => ({
          ...context.calculationData,
          period_id: Number(event.output?.id)
        })) as any
      }),

      /**
       * Assign loaded rules
       */
      assignRules: assign({
        rules: ({ event }: any) => {
          const rules = event.output || [];
          log('usePayrollWizardMachine', 'assignRules', 'Assigning rules to context', {
            rulesCount: rules.length,
            firstRule: rules[0]
          });
          return rules;
        }
      }),

      /**
       * Assign loaded data from resuming state
       * PHASE 2: Now includes advanceDiscounts, deductionDiscounts, and consolidated data structure
       */
      assignLoadedData: assign({
        rules: ({ event }: any) => event.output?.rules || [],
        pendingRefundsCount: ({ event }: any) => event.output?.pendingRefundsCount || 0,
        calculationsComplete: ({ event }: any) => event.output?.calculationsComplete || false,
        paymentByUser: ({ event }: any) => event.output?.paymentByUser || [],
        // Legacy fields (for backward compatibility during migration)
        advanceDiscounts: ({ event }: any) => {
          const discounts = event.output?.advanceDiscounts || [];
          log('usePayrollWizardMachine', 'assignLoadedData', '📥 Assigning advance discounts', {
            count: discounts.length,
            items: discounts.map((d: any) => ({ advance_id: d.advance_id, amount: d.amount }))
          });
          return discounts;
        },
        advanceDiscountsTotal: ({ event }: any) => event.output?.advanceDiscountsTotal || 0,
        deductionDiscounts: ({ event }: any) => {
          const discounts = event.output?.deductionDiscounts || [];
          log('usePayrollWizardMachine', 'assignLoadedData', '📥 Assigning deduction discounts', {
            count: discounts.length,
            items: discounts.map((d: any) => ({ deduction_id: d.deduction_id, amount: d.amount }))
          });
          return discounts;
        },
        deductionDiscountsTotal: ({ event }: any) => event.output?.deductionDiscountsTotal || 0,
        // PHASE 2: Populate consolidated data structure (if available from event.output)
        // Note: This will be populated by the new loading services (loadAdvancesData, etc.)
        'data.advances.available': (({ event }: any) => event.output?.advancesData?.advances || []) as any,
        'data.advances.cash_recebimentos': (({ event }: any) => event.output?.advancesData?.available_cash_recebimentos || []) as any,
        'data.advances.summary_by_employee': (({ event }: any) => event.output?.advancesData?.summary_by_employee || []) as any,
        'data.advances.selected': (({ event }: any) => event.output?.advanceDiscounts || []) as any,
        'data.advances.total': (({ event }: any) => event.output?.advanceDiscountsTotal || 0) as any,
        'data.deductions.available': (({ event }: any) => event.output?.deductionsData?.deductions || []) as any,
        'data.deductions.summary_by_employee': (({ event }: any) => event.output?.deductionsData?.summary_by_employee || []) as any,
        'data.deductions.selected': (({ event }: any) => event.output?.deductionDiscounts || []) as any,
        'data.deductions.total': (({ event }: any) => event.output?.deductionDiscountsTotal || 0) as any,
        'data.refunds.available': (({ event }: any) => event.output?.refundsData?.refunds || []) as any,
        'data.refunds.pending': (({ event }: any) => event.output?.refundsData?.refunds || []) as any,
        'data.refunds.summary_by_user': (({ event }: any) => event.output?.refundsData?.summary_by_user || []) as any,
        'data.refunds.total_pending': (({ event }: any) => event.output?.refundsData?.total_pending || 0) as any,
        // Set flags indicating data has been loaded
        'flags.advances_loaded': (({ event }: any) => !!event.output?.advancesData) as any,
        'flags.deductions_loaded': (({ event }: any) => !!event.output?.deductionsData) as any,
        'flags.refunds_loaded': (({ event }: any) => !!event.output?.refundsData) as any
      }),

      /**
       * Update revenue conciliation data
       */
      updateRevenueData: assign({
        calculationData: ({ context, event }: any) => ({
          ...context.calculationData,
          revenue_conciliation_status: event.status || 'pending',
          revenue_conciliation_amount: event.amount || 0
        })
      }),

      /**
       * Update expense conciliation data
       */
      updateExpensesData: assign({
        calculationData: ({ context, event }: any) => ({
          ...context.calculationData,
          expenses_conciliation_status: event.status || 'pending',
          expenses_conciliation_amount: event.amount || 0
        })
      }),

      /**
       * Assign advance discounts from employee advances step
       * Called when user selects advances to discount
       * PHASE 2: Also updates consolidated data structure (context.data.advances)
       */
      assignAdvanceDiscounts: assign({
        // Legacy field (for backward compatibility during migration)
        advanceDiscounts: ({ context, event }: any) => {
          log('usePayrollWizardMachine', 'assignAdvanceDiscounts', '📥 ASSIGNING ADVANCE DISCOUNTS', {
            eventType: event.type,
            incomingDiscounts: JSON.stringify(event.advanceDiscounts),
            incomingDiscountsCount: event.advanceDiscounts?.length || 0,
            incomingTotal: event.advanceDiscountsTotal,
            previousDiscounts: JSON.stringify(context.advanceDiscounts),
            previousDiscountsCount: context.advanceDiscounts?.length || 0,
            previousTotal: context.advanceDiscountsTotal
          });

          // PAYROLL_REFACTOR logging for context update
          log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'CONTEXT_UPDATE', {
            action: 'assignAdvanceDiscounts',
            updating: ['context.advanceDiscounts', 'context.data.advances.selected', 'context.data.advances.total'],
            from: event.type,
            data: {
              selectedCount: event.advanceDiscounts?.length || 0,
              total: event.advanceDiscountsTotal || 0
            },
            oldFormat: true,
            newFormat: true
          });

          return event.advanceDiscounts || [];
        },
        advanceDiscountsTotal: ({ event }: any) => event.advanceDiscountsTotal || 0,
        // PHASE 2: Update consolidated data structure
        'data.advances.selected': (({ event }: any) => event.advanceDiscounts || []) as any,
        'data.advances.total': (({ event }: any) => event.advanceDiscountsTotal || 0) as any,
        'flags.has_changes': (() => true) as any
      }),

      /**
       * PHASE 3: Assign advance selections from wizard
       * Converts selections object { advance_id: amount } to discounts array format
       * Used by refactored StepEmployeeAdvances component
       */
      assignAdvanceSelections: assign({
        advanceDiscounts: (({ context, event }: any) => {
          log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'CONTEXT_UPDATE', {
            action: 'assignAdvanceSelections',
            updating: 'context.advanceDiscounts',
            from: 'UPDATE_ADVANCE_SELECTIONS event',
            inputFormat: 'object { advance_id: amount }',
            outputFormat: 'array [{ advance_id, amount }]'
          });

          // Convert selections object to discounts array (only items with amount > 0)
          const selections = event.selections || {};
          const discounts = Object.entries(selections)
            .filter(([_, amount]: any) => amount > 0)
            .map(([advanceId, amount]: any) => ({
              advance_id: String(advanceId),
              amount: parseFloat(amount)
            }));

          log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'SELECTIONS_CONVERTED', {
            inputSelections: Object.keys(selections).length,
            outputDiscounts: discounts.length,
            totalAmount: discounts.reduce((sum: any, d: any) => sum + d.amount, 0)
          });

          return discounts;
        }) as any,
        advanceDiscountsTotal: (({ context, event }: any) => {
          const selections = event.selections || {};
          return Object.values(selections)
            .reduce((sum: any, amount: any) => sum + (parseFloat(amount) || 0), 0);
        }) as any,
        // PHASE 2: Update consolidated data structure
        'data.advances.selected': (({ event }: any) => {
          const selections = event.selections || {};
          return Object.entries(selections)
            .filter(([_, amount]: any) => amount > 0)
            .map(([advanceId, amount]: any) => ({
              advance_id: String(advanceId),
              amount: parseFloat(amount)
            }));
        }) as any,
        'data.advances.total': (({ context, event }: any) => {
          const selections = event.selections || {};
          return Object.values(selections)
            .reduce((sum: any, amount: any) => sum + (parseFloat(amount) || 0), 0);
        }) as any,
        'flags.has_changes': (() => true) as any
      }),

      /**
       * Assign deduction discounts from employee deductions step
       * Called when user selects deductions to discount
       * PHASE 2: Also updates consolidated data structure (context.data.deductions)
       */
      assignDeductionDiscounts: assign({
        // Legacy field (for backward compatibility during migration)
        deductionDiscounts: (({ context, event }: any) => {
          log('usePayrollWizardMachine', 'assignDeductionDiscounts', '📥 ASSIGNING DEDUCTION DISCOUNTS', {
            eventType: event.type,
            incomingDiscounts: JSON.stringify(event.deductionDiscounts),
            incomingDiscountsCount: event.deductionDiscounts?.length || 0,
            incomingTotal: event.deductionDiscountsTotal,
            previousDiscounts: JSON.stringify(context.deductionDiscounts),
            previousDiscountsCount: context.deductionDiscounts?.length || 0,
            previousTotal: context.deductionDiscountsTotal
          });

          // PAYROLL_REFACTOR logging for context update
          log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'CONTEXT_UPDATE', {
            action: 'assignDeductionDiscounts',
            updating: ['context.deductionDiscounts', 'context.data.deductions.selected', 'context.data.deductions.total'],
            from: event.type,
            data: {
              selectedCount: event.deductionDiscounts?.length || 0,
              total: event.deductionDiscountsTotal || 0
            },
            oldFormat: true,
            newFormat: true
          });

          return event.deductionDiscounts || [];
        }) as any,
        deductionDiscountsTotal: (({ event }: any) => event.deductionDiscountsTotal || 0) as any,
        // PHASE 2: Update consolidated data structure
        'data.deductions.selected': (({ event }: any) => event.deductionDiscounts || []) as any,
        'data.deductions.total': (({ event }: any) => event.deductionDiscountsTotal || 0) as any,
        'flags.has_changes': (() => true) as any
      }),

      /**
       * PHASE 3: Assign deduction selections from wizard
       * Converts selections object { deduction_id: amount } to discounts array format
       * Used by refactored StepEmployeeDeductions component
       */
      assignDeductionSelections: assign({
        deductionDiscounts: (({ context, event }: any) => {
          log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'CONTEXT_UPDATE', {
            action: 'assignDeductionSelections',
            updating: 'context.deductionDiscounts',
            from: 'UPDATE_DEDUCTION_SELECTIONS event',
            inputFormat: 'object { deduction_id: amount }',
            outputFormat: 'array [{ deduction_id, amount }]'
          });

          // Convert selections object to discounts array (only items with amount > 0)
          const selections = event.selections || {};
          const discounts = Object.entries(selections)
            .filter(([_, amount]: any) => amount > 0)
            .map(([deductionId, amount]: any) => ({
              deduction_id: String(deductionId),
              amount: parseFloat(amount)
            }));

          log('PAYROLL_REFACTOR', 'MACHINE_STATE', 'SELECTIONS_CONVERTED', {
            inputSelections: Object.keys(selections).length,
            outputDiscounts: discounts.length,
            totalAmount: discounts.reduce((sum: any, d: any) => sum + d.amount, 0)
          });

          return discounts;
        }) as any,
        deductionDiscountsTotal: (({ context, event }: any) => {
          const selections = event.selections || {};
          return Object.values(selections)
            .reduce((sum: any, amount: any) => sum + (parseFloat(amount) || 0), 0);
        }) as any,
        // PHASE 2: Update consolidated data structure
        'data.deductions.selected': (({ event }: any) => {
          const selections = event.selections || {};
          return Object.entries(selections)
            .filter(([_, amount]: any) => amount > 0)
            .map(([deductionId, amount]: any) => ({
              deduction_id: String(deductionId),
              amount: parseFloat(amount)
            }));
        }) as any,
        'data.deductions.total': (({ context, event }: any) => {
          const selections = event.selections || {};
          return Object.values(selections)
            .reduce((sum: any, amount: any) => sum + (parseFloat(amount) || 0), 0);
        }) as any,
        'flags.has_changes': (() => true) as any
      }),

      /**
       * Assign calculation results (from machine's calculating invoke)
       */
      assignCalculations: assign({
        calculationsComplete: true,
        calculationSummary: ({ event }: any) => ({
          executed: event.output?.executed || 0,
          soldRemotely: event.output?.soldRemotely || 0,
          fixed: event.output?.fixed || 0,
          total: event.output?.total || 0
        }),
        paymentByUser: ({ event }: any) => event.output?.by_user || [],
        period: ({ context, event }: any) => ({
          ...(context.period as any),
          status: event.output?.status || PERIOD_STATUS.CALCULATED
        })
      }),

      /**
       * Assign calculation results from dialog (CALCULATIONS_DONE event)
       * Called when PayrollCalculationsDialog completes calculations
       */
      assignCalculationsFromDialog: assign({
        calculationsComplete: true,
        calculationSummary: ({ event }: any) => ({
          executed: event.executed || 0,
          soldRemotely: event.soldRemotely || 0,
          fixed: event.fixed || 0,
          total: event.total || 0
        }),
        paymentByUser: ({ event }: any) => event.by_user || [],
        period: ({ context, event }: any) => ({
          ...(context.period as any),
          status: event.status || PERIOD_STATUS.CALCULATED
        })
      }),

      /**
       * Assign confirmation result
       */
      assignConfirmation: assign({
        confirmationResult: ({ event }: any) => ({
          success: true,
          error: null,
          despesas: event.output?.despesas || []
        }),
        period: ({ context, event }: any) => ({
          ...(context.period as any),
          status: event.output?.status || PERIOD_STATUS.CONFIRMED
        })
      }),

      /**
       * PHASE 2: Assign advances data from loading state
       * Populates consolidated data structure with loaded advances
       */
      assignAdvancesData: assign({
        'data.advances.available': (({ event }: any) => {
          const advances = event.output?.advances || [];
          log('usePayrollWizardMachine', 'assignAdvancesData', '📥 Assigning advances data', {
            count: advances.length,
            totalOpen: event.output?.total_open || 0,
            cashRecebimentosCount: event.output?.available_cash_recebimentos?.length || 0
          });
          return advances;
        }) as any,
        'data.advances.cash_recebimentos': (({ event }: any) => event.output?.available_cash_recebimentos || []) as any,
        'data.advances.summary_by_employee': (({ event }: any) => event.output?.summary_by_employee || []) as any,
        'flags.advances_loaded': (() => true) as any
      }),

      /**
       * PHASE 2: Assign deductions data from loading state
       * Populates consolidated data structure with loaded deductions
       */
      assignDeductionsData: assign({
        'data.deductions.available': (({ event }: any) => {
          const deductions = event.output?.deductions || [];
          log('usePayrollWizardMachine', 'assignDeductionsData', '📥 Assigning deductions data', {
            count: deductions.length,
            totalOpen: event.output?.total_open || 0
          });
          return deductions;
        }) as any,
        'data.deductions.summary_by_employee': (({ event }: any) => event.output?.summary_by_employee || []) as any,
        'flags.deductions_loaded': (() => true) as any
      }),

      /**
       * PHASE 2: Assign refunds data from loading state
       * Populates consolidated data structure with loaded refunds
       */
      assignRefundsData: assign({
        'data.refunds.available': (({ event }: any) => {
          const refunds = event.output?.refunds || [];
          log('usePayrollWizardMachine', 'assignRefundsData', '📥 Assigning refunds data', {
            count: refunds.length,
            totalPending: event.output?.total_pending || 0
          });
          return refunds;
        }) as any,
        'data.refunds.pending': (({ event }: any) => event.output?.refunds || []) as any,
        'data.refunds.summary_by_user': (({ event }: any) => event.output?.summary_by_user || []) as any,
        'data.refunds.total_pending': (({ event }: any) => event.output?.total_pending || 0) as any,
        'flags.refunds_loaded': (() => true) as any
      }),

      /**
       * PHASE 2: Assign advances error from failed loading
       */
      assignAdvancesError: assign({
        error: ({ event }: any) => {
          const errorMsg = event.error?.message || 'Falha ao carregar adiantamentos';
          log('usePayrollWizardMachine', 'assignAdvancesError', '❌ Error loading advances', {
            error: errorMsg
          });
          return errorMsg;
        }
      }),

      /**
       * PHASE 2: Assign deductions error from failed loading
       */
      assignDeductionsError: assign({
        error: ({ event }: any) => {
          const errorMsg = event.error?.message || 'Falha ao carregar deduções';
          log('usePayrollWizardMachine', 'assignDeductionsError', '❌ Error loading deductions', {
            error: errorMsg
          });
          return errorMsg;
        }
      }),

      /**
       * PHASE 2: Assign refunds error from failed loading
       */
      assignRefundsError: assign({
        error: ({ event }: any) => {
          const errorMsg = event.error?.message || 'Falha ao carregar reembolsos';
          log('usePayrollWizardMachine', 'assignRefundsError', '❌ Error loading refunds', {
            error: errorMsg
          });
          return errorMsg;
        }
      }),

      /**
       * Assign error from failed operation
       */
      assignError: assign({
        error: ({ event }: any) => event.error?.message || 'Unknown error'
      }),

      /**
       * Emit saved event for external listeners
       */
      emitSaved: ({ context }: any) => {
        log('usePayrollWizardMachine', 'emitSaved', 'Period saved', {
          periodId: (context.period as any)?.id
        });
        eventBus.emit('payroll-period.saved', (context.period as any)?.id);
      }
    }
  });
};

// =============================================================================
// COMPOSABLE
// =============================================================================

/**
 * Vue composable for the payroll wizard state machine
 *
 * Provides reactive access to machine state and context,
 * plus helper methods for sending events.
 *
 * @param {Object} services - Service implementations for async operations
 * @returns {Object} Machine state, context, and methods
 */
export function usePayrollWizardMachine(services = {}) {
  const { t } = useI18n();

  // Instantiate composables for service implementations
  const { createPeriod: createPeriodApi } = usePayrollPeriods();
  const { loadRulesPreviewByUser } = usePayrollRulesEngine();
  const {
    calculateCommissionOverGrossExecuted,
    calculateCommissionOverGrossSoldRemotely,
    calculateFixed,
    markPeriodCalculated,
    getPeriodCalculationSummary,
    confirmPeriod: confirmPeriodApi
  } = usePayrollCalculations();
  const { getOpenAdvancesForPeriod } = useEmployeeAdvances();
  const { getOpenDeductionsForPeriod } = useEmployeeDeductions();

  // Create machine with provided services
  const machine = createPayrollWizardMachine();

  // Default service implementations using actual API composables
  const defaultServices = {
    /**
     * Create period in backend
     * Uses usePayrollPeriods.createPeriod()
     */
    createPeriod: fromPromise(async ({ input }: any) => {
      const { context } = input;
      const { periodFormData } = context;

      log('usePayrollWizardMachine', 'createPeriod', 'Creating period', {
        franchiseId: periodFormData.franchise_id,
        frequency: periodFormData.frequency,
        startDate: periodFormData.start_date,
        endDate: periodFormData.end_date
      });

      const period = await createPeriodApi(
        periodFormData.franchise_id,
        periodFormData.frequency,
        periodFormData.start_date,
        periodFormData.end_date
      );

      log('usePayrollWizardMachine', 'createPeriod', 'Period created', { periodId: (period as any)?.id });
      return period;
    }),

    /**
     * Load rules for period after creation
     * Uses usePayrollRulesEngine.loadRulesPreviewByUser() (employee-centered)
     */
    loadRules: fromPromise(async ({ input }: any) => {
      const { context } = input;
      const franchiseId = (context as any).period?.franchise_id || (context as any).periodFormData?.franchise_id;
      const userId = (context as any).period?.user_id || (context as any).periodFormData?.user_id;

      log('usePayrollWizardMachine', 'loadRules', 'Loading rules by user', { franchiseId, userId });

      const rules = await loadRulesPreviewByUser(franchiseId, userId);

      log('usePayrollWizardMachine', 'loadRules', 'Rules loaded', { count: rules?.length });
      return rules;
    }),

    /**
     * Load period data for edit mode (resuming existing period)
     * Loads rules based on period's franchise and user_id (employee-centered)
     * Also loads paymentByUser if period has been calculated (needed for deductions step)
     * Also loads advanceDiscounts (already selected advance items for this period)
     */
    loadPeriodData: fromPromise(async ({ input }: any) => {
      const { context } = input;
      const period = (context as any).period;

      if (!period) {
        throw new Error('No period provided for loading data');
      }

      const periodId = Number((period as any).id);
      const franchiseId = Number((period as any).franchise_id);
      const userId = (period as any).user_id;
      const periodStatus = parseInt((period as any).status);
      const isCalculated = periodStatus >= PERIOD_STATUS.CALCULATED;

      log('usePayrollWizardMachine', 'loadPeriodData', 'Loading period data', {
        periodId,
        franchiseId,
        userId,
        status: periodStatus,
        isCalculated
      });

      // Load rules for the period (employee-centered: by user_id)
      const rules = await loadRulesPreviewByUser(franchiseId, userId);

      // If period has been calculated, also load paymentByUser data
      // This is needed for the deductions step to show users
      let paymentByUser: unknown[] = [];
      if (isCalculated) {
        try {
          const summary = await getPeriodCalculationSummary(periodId, franchiseId);
          // TODO(contract-debt): Payroll_PeriodCalculationSummaryResponse.summary is
          // shape-erased to `object` (see @shape-erased-exception in the PHP contract);
          // by_user lives inside that aggregate.
          const summaryView = summary as { by_user?: unknown[] } | undefined;
          paymentByUser = summaryView?.by_user || [];
          log('usePayrollWizardMachine', 'loadPeriodData', 'Loaded payment by user', {
            userCount: paymentByUser.length
          });
        } catch (err) {
          log('usePayrollWizardMachine', 'loadPeriodData', 'Failed to load payment by user', {
            error: err.message
          });
        }
      }

      // NEW: Load advance discounts (already selected items for this period)
      // The advance and deduction `selected_items` rows share the same
      // logical role here (an amount + a title pulled into the wizard);
      // each has a domain-specific id field (advance_id / deduction_id).
      type AdvanceSelectedItem = {
        advance_id: number;
        amount: number;
        title: string;
        advance_amount: number;
        employee_id?: string;
        employee_name?: string;
      };
      type DeductionSelectedItem = {
        deduction_id: number;
        amount: number;
        deduction_amount: number;
        title?: string;
        employee_id?: string;
        employee_name?: string;
      };
      let advanceDiscounts: AdvanceSelectedItem[] = [];
      let advanceDiscountsTotal = 0;
      try {
        const advancesData = await getOpenAdvancesForPeriod(periodId);
        if (advancesData.selected_items && advancesData.selected_items.length > 0) {
          advanceDiscounts = advancesData.selected_items;
          advanceDiscountsTotal = advanceDiscounts.reduce((sum: any, item: any) => sum + (item.amount || 0), 0);
          log('usePayrollWizardMachine', 'loadPeriodData', '✅ Loaded advance discounts from selected_items', {
            count: advanceDiscounts.length,
            total: advanceDiscountsTotal,
            items: advanceDiscounts.map((d: any) => ({ advance_id: d.advance_id, amount: d.amount }))
          });
        } else {
          log('usePayrollWizardMachine', 'loadPeriodData', 'No selected_items found for advances', {
            periodId,
            selectedItemsCount: advancesData.selected_items?.length || 0
          });
        }
      } catch (err) {
        log('usePayrollWizardMachine', 'loadPeriodData', 'Failed to load advance discounts', {
          error: err.message
        });
      }

      // NEW: Load deduction discounts (already selected items for this period)
      let deductionDiscounts: DeductionSelectedItem[] = [];
      let deductionDiscountsTotal = 0;
      try {
        const deductionsData = await getOpenDeductionsForPeriod(periodId);
        if (deductionsData.selected_items && deductionsData.selected_items.length > 0) {
          deductionDiscounts = deductionsData.selected_items;
          deductionDiscountsTotal = deductionDiscounts.reduce((sum: any, item: any) => sum + (item.amount || 0), 0);
          log('usePayrollWizardMachine', 'loadPeriodData', '✅ Loaded deduction discounts from selected_items', {
            count: deductionDiscounts.length,
            total: deductionDiscountsTotal,
            items: deductionDiscounts.map((d: any) => ({ deduction_id: d.deduction_id, amount: d.amount }))
          });
        } else {
          log('usePayrollWizardMachine', 'loadPeriodData', 'No selected_items found for deductions', {
            periodId,
            selectedItemsCount: deductionsData.selected_items?.length || 0
          });
        }
      } catch (err) {
        log('usePayrollWizardMachine', 'loadPeriodData', 'Failed to load deduction discounts', {
          error: err.message
        });
      }

      log('usePayrollWizardMachine', 'loadPeriodData', 'Period data loaded', {
        rulesCount: rules?.length,
        periodStatus,
        paymentByUserCount: paymentByUser.length,
        advanceDiscountsCount: advanceDiscounts.length,
        advanceDiscountsTotal,
        deductionDiscountsCount: deductionDiscounts.length,
        deductionDiscountsTotal
      });

      return {
        rules: rules || [],
        pendingRefundsCount: 0, // TODO: Load from API if needed
        calculationsComplete: isCalculated,
        paymentByUser,
        // NEW: Include advance discounts for resuming periods
        advanceDiscounts,
        advanceDiscountsTotal,
        // NEW: Include deduction discounts for resuming periods
        deductionDiscounts,
        deductionDiscountsTotal
      };
    }),

    /**
     * Run all payment calculations sequentially
     * Executes: executed → sold remotely → fixed → mark calculated → get summary
     */
    runCalculations: fromPromise(async ({ input }: any) => {
      const { context } = input;
      const periodId = (context as any).period?.id;
      const franchiseId = (context as any).period?.franchise_id || (context as any).periodFormData?.franchise_id;

      if (!periodId || !franchiseId) {
        throw new Error('Period ID and Franchise ID required for calculations');
      }

      log('usePayrollWizardMachine', 'runCalculations', 'Starting calculations', {
        periodId,
        franchiseId
      });

      // Run all calculation steps sequentially
      // Step 1: Commission over gross revenue EXECUTED
      log('usePayrollWizardMachine', 'runCalculations', 'Step 1: Calculating executed commissions');
      const executedResult = await calculateCommissionOverGrossExecuted(periodId, franchiseId);

      // Step 2: Commission over gross revenue SOLD REMOTELY
      log('usePayrollWizardMachine', 'runCalculations', 'Step 2: Calculating sold remotely commissions');
      const soldRemotelyResult = await calculateCommissionOverGrossSoldRemotely(periodId, franchiseId);

      // Step 3: Fixed rules
      log('usePayrollWizardMachine', 'runCalculations', 'Step 3: Calculating fixed rules');
      const fixedResult = await calculateFixed(periodId, franchiseId);

      // Step 4: Mark period as calculated
      log('usePayrollWizardMachine', 'runCalculations', 'Step 4: Marking period as calculated');
      await markPeriodCalculated(periodId, franchiseId);

      // Step 5: Get calculation summary
      log('usePayrollWizardMachine', 'runCalculations', 'Step 5: Getting calculation summary');
      const summary = await getPeriodCalculationSummary(periodId, franchiseId);
      // TODO(contract-debt): Payroll_PeriodCalculationSummaryResponse.summary is
      // shape-erased to `object` (see @shape-erased-exception in the PHP contract);
      // by_user lives inside that aggregate.
      const summaryView = summary as { by_user?: unknown[] } | undefined;

      // The Payroll_Calculate*Response contracts intentionally type `totals`
      // as Record<string, unknown> because the per-rule shapes vary; coerce
      // to number at the consumer boundary.
      const executedTotal = Number(executedResult?.totals?.commission_over_gross_executed) || 0;
      const soldRemotelyTotal = Number(soldRemotelyResult?.totals?.commission_over_gross_sold_remotely) || 0;
      const fixedTotal = Number(fixedResult?.totals?.fixed) || 0;

      log('usePayrollWizardMachine', 'runCalculations', 'Calculations complete', {
        executed: executedTotal,
        soldRemotely: soldRemotelyTotal,
        fixed: fixedTotal,
        byUserCount: summaryView?.by_user?.length || 0
      });

      return {
        executed: executedTotal,
        soldRemotely: soldRemotelyTotal,
        fixed: fixedTotal,
        total: executedTotal + soldRemotelyTotal + fixedTotal,
        by_user: summaryView?.by_user || [],
        status: PERIOD_STATUS.CALCULATED
      };
    }),

    /**
     * Confirm period and create despesas
     * Uses usePayrollCalculations.confirmPeriod()
     */
    confirmPeriod: fromPromise(async ({ input }: any) => {
      const { context } = input;
      const periodId = (context as any).period?.id;
      const franchiseId = (context as any).period?.franchise_id || (context as any).periodFormData?.franchise_id;

      if (!periodId || !franchiseId) {
        throw new Error('Period ID and Franchise ID required for confirmation');
      }

      log('usePayrollWizardMachine', 'confirmPeriod', 'Confirming period', {
        periodId,
        franchiseId
      });

      const result = await confirmPeriodApi(periodId, franchiseId);

      log('usePayrollWizardMachine', 'confirmPeriod', 'Period confirmed', {
        despesaCount: result?.despesas?.length || 0,
        status: result?.status
      });

      return {
        success: true,
        despesas: result?.despesas || [],
        status: result?.status || PERIOD_STATUS.CONFIRMED
      };
    }),

    // =========================================================================
    // PHASE 2: Consolidated Data Loading Services
    // These services use the repository layer and populate the consolidated data structure
    // =========================================================================

    /**
     * Load advances data using repository
     * PHASE 2: New service that uses payrollRepository instead of composable
     * Populates context.data.advances with available data and selected items
     */
    loadAdvancesData: fromPromise(async ({ input }: any) => {
      const { context } = input;
      const periodId = (context as any).period?.id;

      if (!periodId) {
        throw new Error('Period ID required to load advances data');
      }

      log('usePayrollWizardMachine', 'loadAdvancesData', 'Loading advances from repository', { periodId });

      const data = await payrollRepository.getOpenAdvancesForPeriod(periodId);

      log('usePayrollWizardMachine', 'loadAdvancesData', 'Advances loaded', {
        advancesCount: data.advances.length,
        cashRecebimentosCount: data.available_cash_recebimentos.length,
        selectedItemsCount: data.selected_items.length,
        totalOpen: data.total_open
      });

      return data;
    }),

    /**
     * Load deductions data using repository
     * PHASE 2: New service that uses payrollRepository instead of composable
     * Populates context.data.deductions with available data and selected items
     */
    loadDeductionsData: fromPromise(async ({ input }: any) => {
      const { context } = input;
      const periodId = (context as any).period?.id;

      if (!periodId) {
        throw new Error('Period ID required to load deductions data');
      }

      log('usePayrollWizardMachine', 'loadDeductionsData', 'Loading deductions from repository', { periodId });

      const data = await payrollRepository.getOpenDeductionsForPeriod(periodId);

      log('usePayrollWizardMachine', 'loadDeductionsData', 'Deductions loaded', {
        deductionsCount: data.deductions.length,
        selectedItemsCount: data.selected_items.length,
        totalOpen: data.total_open
      });

      return data;
    }),

    /**
     * Load refunds data using repository
     * PHASE 2: New service that uses payrollRepository instead of composable
     * Populates context.data.refunds with available and pending data
     */
    loadRefundsData: fromPromise(async ({ input }: any) => {
      const { context } = input;
      const periodId = (context as any).period?.id;

      if (!periodId) {
        throw new Error('Period ID required to load refunds data');
      }

      log('usePayrollWizardMachine', 'loadRefundsData', 'Loading refunds from repository', { periodId });

      const data = await payrollRepository.getPendingRefundsForPeriod(periodId);

      log('usePayrollWizardMachine', 'loadRefundsData', 'Refunds loaded', {
        refundsCount: data.refunds.length,
        summaryCount: data.summary_by_user.length,
        totalPending: data.total_pending
      });

      return data;
    }),

    ...services
  };

  // Use the machine with XState Vue integration
  // NOTE: XState v5 uses machine.provide() to inject actors at runtime
  // This replaces the v4 pattern of passing 'services' to useMachine()
  const machineWithActors = machine.provide({
    actors: defaultServices
  });

  const { snapshot, send, actorRef } = useMachine(machineWithActors);

  // ==========================================================================
  // COMPUTED HELPERS
  // ==========================================================================

  /**
   * Current state value (e.g., 'config', 'rules', 'calculating')
   */
  const currentState = computed(() => {
    const state = snapshot.value;
    if (typeof state.value === 'string') {
      return state.value;
    }
    // Handle compound states
    return Object.keys(state.value)[0] || 'idle';
  });

  /**
   * Current context (all wizard data)
   */
  const context = computed(() => snapshot.value.context);

  /**
   * Is currently in a loading/processing state
   */
  const isLoading = computed(() => {
    const loadingStates = [
      'creatingPeriod',
      'loadingRules',
      'resuming',
      'calculating',
      'confirming'
    ];
    return loadingStates.includes(currentState.value);
  });

  /**
   * Current period object
   */
  const period = computed(() => context.value.period);

  /**
   * Is in edit mode
   */
  const isEditMode = computed(() => context.value.isEditMode);

  /**
   * Period form data
   */
  const periodFormData = computed(() => context.value.periodFormData);

  /**
   * Payment rules
   */
  const rules = computed(() => context.value.rules);

  /**
   * Has gross revenue rules
   */
  const hasGrossRevenueRules = computed(() => {
    return rules.value.some((rule: any) =>
      (rule as any).rule_type?.startsWith('commission_over_gross_revenue')
    );
  });

  /**
   * Has commission rules (for advances)
   */
  const hasCommissionRules = computed(() => {
    return rules.value.some((rule: any) =>
      (rule as any).rule_type === 'commission_over_gross_revenue_executed'
    );
  });

  /**
   * Calculation summary
   */
  const calculationSummary = computed(() => context.value.calculationSummary);

  /**
   * Calculations complete flag
   */
  const calculationsComplete = computed(() => context.value.calculationsComplete);

  /**
   * Payment by user
   */
  const paymentByUser = computed(() => context.value.paymentByUser);

  /**
   * Pending refunds count
   */
  const pendingRefundsCount = computed(() => context.value.pendingRefundsCount);

  /**
   * Confirmation result
   */
  const confirmationResult = computed(() => context.value.confirmationResult);

  /**
   * Current error (if any)
   */
  const error = computed(() => context.value.error);

  // ==========================================================================
  // STEP MANAGEMENT (Replaces stepRegistry from usePayrollWizard.js)
  // ==========================================================================

  /**
   * Dynamic list of visible steps based on machine context
   * Replaces the stepRegistry filtering logic from usePayrollWizard.js
   *
   * Steps are filtered based on:
   * - alwaysShow: Step is always visible
   * - condition: Function that evaluates against context
   *
   * @returns {Array<{key: string, title: string}>}
   */
  const visibleSteps = computed(() => {
    const ctx = context.value as any;

    const allSteps = [
      {
        key: 'config',
        title: t('Configuração do período'),
        alwaysShow: true
      },
      {
        key: 'rules',
        title: t('Regras de cálculo'),
        alwaysShow: true
      },
      {
        key: 'revenue',
        title: t('Conciliação de receita'),
        condition: () => hasGrossRevenueRules.value
      },
      // OLD: advances removed - unified into employeeAdvances
      {
        key: 'employeeAdvances',
        title: t('Adiantamentos de Colaboradores'),
        condition: () => !!ctx.period?.id
      },
      {
        key: 'refunds',
        title: t('Reembolsos'),
        condition: () => (ctx.pendingRefundsCount || 0) > 0
      },
      {
        key: 'employeeDeductions',
        title: t('Deduções de Colaboradores'),
        condition: () => !!ctx.period?.id
      },
      {
        key: 'payment',
        title: t('Cálculos'),
        condition: () => !!ctx.period?.id
      },
      {
        key: 'summary',
        title: t('Resumo e Confirmação'),
        condition: () => ctx.calculationsComplete
      },
      {
        key: 'finish',
        title: t('Finalização'),
        condition: () => !!(ctx.confirmationResult?.success || ctx.confirmationResult?.error)
      }
    ];

    return allSteps.filter(step => step.alwaysShow || (step.condition && step.condition()));
  });

  /**
   * Get the Vue component for the current machine state
   * Used for dynamic component rendering: <component :is="currentStepComponent" />
   *
   * @returns {Component|null}
   */
  const currentStepComponent = computed(() => {
    const stepKey = getStepKeyFromState(currentState.value);
    return (STEP_COMPONENTS as any)[stepKey] || null;
  });

  /**
   * Get current step index within the visible steps array
   * Used by WizardModal to highlight the correct step indicator
   *
   * @returns {number} Index in visibleSteps array, or 0 if not found
   */
  const currentStepIndex = computed(() => {
    const stepKey = getStepKeyFromState(currentState.value);
    const index = visibleSteps.value.findIndex(s => s.key === stepKey);
    return index >= 0 ? index : 0;
  });

  /**
   * Step registry for WizardModal compatibility
   *
   * Since step components now use inject('wizardMachine') to access data,
   * getProps and getEvents return empty objects.
   *
   * This is a simplified registry that only provides:
   * - component: The Vue component to render
   * - getProps: Returns {} (steps get data via inject)
   * - getEvents: Returns {} (steps emit via eventBus)
   *
   * @type {Object}
   */
  const stepRegistry = {
    config: {
      component: STEP_COMPONENTS.config,
      getProps: () => ({}),
      getEvents: () => ({})
    },
    rules: {
      component: STEP_COMPONENTS.rules,
      getProps: () => ({}),
      getEvents: () => ({})
    },
    revenue: {
      component: STEP_COMPONENTS.revenue,
      getProps: () => ({}),
      getEvents: () => ({})
    },
    // OLD: advances removed - unified into employeeAdvances
    employeeAdvances: {
      component: STEP_COMPONENTS.employeeAdvances,
      getProps: () => ({}),
      getEvents: () => ({})
    },
    refunds: {
      component: STEP_COMPONENTS.refunds,
      getProps: () => ({}),
      getEvents: () => ({})
    },
    employeeDeductions: {
      component: STEP_COMPONENTS.employeeDeductions,
      getProps: () => ({}),
      getEvents: () => ({})
    },
    payment: {
      component: STEP_COMPONENTS.payment,
      getProps: () => ({}),
      getEvents: () => ({})
    },
    summary: {
      component: STEP_COMPONENTS.summary,
      getProps: () => ({}),
      getEvents: () => ({})
    },
    finish: {
      component: STEP_COMPONENTS.finish,
      getProps: () => ({}),
      getEvents: () => ({})
    }
  };

  // ==========================================================================
  // NAVIGATION HELPERS
  // ==========================================================================

  /**
   * Can go to next step (based on current state and guards)
   */
  const canProceed = computed(() => {
    const state = snapshot.value;
    // Check if NEXT event is allowed in current state
    return state.can({ type: 'NEXT' }) || state.can({ type: 'SUBMIT' }) || state.can({ type: 'CONFIRM' });
  });

  /**
   * Can go back (based on current state)
   */
  const canGoBack = computed(() => {
    const state = snapshot.value;
    return state.can({ type: 'BACK' });
  });

  // ==========================================================================
  // METHODS
  // ==========================================================================

  /**
   * Start new wizard (create new period)
   */
  const startNew = () => {
    log('usePayrollWizardMachine', 'startNew', 'Starting new wizard');
    send({ type: 'START_NEW' });
  };

  /**
   * Resume existing period (edit mode)
   *
   * @param {Object} period - Existing period to edit
   */
  const resumePeriod = (period: any) => {
    log('usePayrollWizardMachine', 'resumePeriod', 'Resuming period', { periodId: period?.id });
    send({ type: 'RESUME_PERIOD', period });
  };

  /**
   * Update form data during config step
   *
   * @param {Object} data - Form data to update
   */
  const updateForm = (data: any) => {
    send({ type: 'UPDATE_FORM', data });
  };

  /**
   * Submit config form (create period)
   */
  const submitConfig = () => {
    send({ type: 'SUBMIT' });
  };

  /**
   * Go to next step
   */
  const next = () => {
    log('usePayrollWizardMachine', 'next', 'Sending NEXT event', {
      currentState: currentState.value,
      canNext: snapshot.value.can({ type: 'NEXT' })
    });
    send({ type: 'NEXT' });
  };

  /**
   * Go to previous step
   */
  const back = () => {
    send({ type: 'BACK' });
  };

  /**
   * Confirm period (on summary step)
   */
  const confirm = () => {
    const state = snapshot.value;
    const canConfirm = state.can({ type: 'CONFIRM' });

    log('usePayrollWizardMachine', 'confirm', 'Attempting CONFIRM', {
      currentState: currentState.value,
      canConfirm,
      contextPeriodId: context.value.period?.id,
      calculationsComplete: context.value.calculationsComplete
    });

    if (!canConfirm) {
      log('usePayrollWizardMachine', 'confirm', 'CONFIRM event not allowed in current state', {
        currentState: currentState.value,
        allowedEvents: (state as any).nextEvents
      });
      return;
    }

    send({ type: 'CONFIRM' });
    log('usePayrollWizardMachine', 'confirm', 'CONFIRM event sent');
  };

  /**
   * Start calculations (on payment step)
   * Triggers the calculation process via machine's calculating invoke
   */
  const startCalculations = () => {
    const state = snapshot.value;
    const canStart = state.can({ type: 'START_CALCULATIONS' });

    log('usePayrollWizardMachine', 'startCalculations', 'Attempting START_CALCULATIONS', {
      currentState: currentState.value,
      canStart
    });

    if (!canStart) {
      log('usePayrollWizardMachine', 'startCalculations', 'START_CALCULATIONS not allowed in current state');
      return;
    }

    send({ type: 'START_CALCULATIONS' });
  };

  /**
   * Mark calculations as done (called by PayrollCalculationsDialog)
   * This bypasses the machine's calculating invoke since the dialog already ran calculations
   *
   * @param {Object} calculationData - Results from the dialog
   */
  const markCalculationsDone = (calculationData: any) => {
    const state = snapshot.value;
    const canMark = state.can({ type: 'CALCULATIONS_DONE' });

    log('usePayrollWizardMachine', 'markCalculationsDone', 'Marking calculations as done', {
      currentState: currentState.value,
      canMark,
      calculationData
    });

    if (!canMark) {
      log('usePayrollWizardMachine', 'markCalculationsDone', 'CALCULATIONS_DONE not allowed in current state');
      return;
    }

    send({
      type: 'CALCULATIONS_DONE',
      ...calculationData
    });
  };

  /**
   * Check if the machine actor is stopped (reached final state)
   * In XState v5, actors that reach a final state are automatically stopped
   */
  const isStopped = () => {
    // In XState v5, actorRef.status is 'stopped' when actor reaches final state
    return (actorRef as any).status === 'stopped' || (actorRef.getSnapshot() as any).status === 'stopped';
  };

  /**
   * Cancel wizard - resets machine to idle state
   * No-op if machine is already stopped (reached final state)
   */
  const cancel = () => {
    if (isStopped()) {
      log('usePayrollWizardMachine', 'cancel', 'Machine already stopped, skipping RESET');
      return;
    }
    log('usePayrollWizardMachine', 'cancel', 'Cancelling wizard, resetting to idle');
    send({ type: 'RESET' });
  };

  /**
   * Reset wizard to initial state (can be called from any state)
   * No-op if machine is already stopped (reached final state)
   */
  const reset = () => {
    if (isStopped()) {
      log('usePayrollWizardMachine', 'reset', 'Machine already stopped, skipping RESET');
      return;
    }
    log('usePayrollWizardMachine', 'reset', 'Resetting wizard to idle');
    send({ type: 'RESET' });
  };

  /**
   * Navigate directly to a specific step by index
   * Used by StateMachineWizardModal when allowStepClick is enabled
   *
   * @param {number} stepIndex - Index in visibleSteps array
   */
  const goToStep = (stepIndex: any) => {
    if (isStopped()) {
      log('usePayrollWizardMachine', 'goToStep', 'Machine already stopped, cannot navigate');
      return;
    }

    const steps = visibleSteps.value;
    if (stepIndex < 0 || stepIndex >= steps.length) {
      log('usePayrollWizardMachine', 'goToStep', 'Invalid step index', { stepIndex, stepsCount: steps.length });
      return;
    }

    const targetStep = steps[stepIndex];
    const stepKey = (targetStep as any).key;

    log('usePayrollWizardMachine', 'goToStep', 'Navigating to step', {
      stepIndex,
      stepKey,
      currentState: currentState.value
    });

    // Don't navigate if we're already on that step
    if (getStepKeyFromState(currentState.value) === stepKey) {
      log('usePayrollWizardMachine', 'goToStep', 'Already on target step, skipping');
      return;
    }

    // Don't navigate during loading states
    if (isLoading.value) {
      log('usePayrollWizardMachine', 'goToStep', 'Cannot navigate during loading state');
      return;
    }

    send({ type: 'GO_TO_STEP', stepKey });
  };

  /**
   * Update revenue conciliation data
   *
   * @param {string} status - Conciliation status
   * @param {number} amount - Conciliation amount
   */
  const updateRevenue = (status: any, amount: any) => {
    send({ type: 'UPDATE_REVENUE', status, amount });
  };

  /**
   * Update expense conciliation data
   *
   * @param {string} status - Conciliation status
   * @param {number} amount - Conciliation amount
   */
  const updateExpenses = (status: any, amount: any) => {
    send({ type: 'UPDATE_EXPENSES', status, amount });
  };

  /**
   * Get next button label based on step key
   * When called with no argument, uses machine state (legacy)
   * When called with stepKey, returns label for that step (preferred)
   *
   * @param {string} [stepKey] - Optional step key to get label for
   * @returns {string} Button label
   */
  const getNextLabel = (stepKey = null) => {
    // If stepKey provided, use it; otherwise fall back to machine state
    const key = stepKey || getStepKeyFromState(currentState.value);

    switch (key) {
      case 'finish':
        return t('Fechar');
      case 'summary':
        return t('Confirmar e Criar Despesas');
      case 'config':
        return isEditMode.value ? t('Próximo') : t('Criar Período');
      default:
        return t('Próximo');
    }
  };

  /**
   * Get next button label for a specific wizard step index
   * Used by WizardModal to show correct label for displayed step
   *
   * @param {number} stepIndex - The wizard step index
   * @returns {string} Button label
   */
  const getNextLabelForStep = (stepIndex: any) => {
    const steps = visibleSteps.value;
    if (stepIndex >= 0 && stepIndex < steps.length) {
      return getNextLabel((steps[stepIndex] as any).key);
    }
    return t('Próximo');
  };

  /**
   * Map state to step index for WizardModal compatibility
   *
   * @returns {number} Step index (0-based)
   */
  const getStepIndex = () => {
    const stateToIndex = {
      idle: -1,
      config: 0,
      creatingPeriod: 0,
      loadingRules: 0,
      rules: 1,
      revenue: 2,
      // OLD: advances removed - unified into employeeAdvances
      employeeAdvances: 3,
      refunds: 4,
      employeeDeductions: 5,
      payment: 6,
      calculating: 6,
      summary: 7,
      confirming: 7,
      finished: 8,
      resuming: -1
    };
    return (stateToIndex as any)[currentState.value] ?? 0;
  };

  /**
   * Check if a specific state is active
   *
   * @param {string} state - State to check
   * @returns {boolean}
   */
  const isInState = (state: any) => {
    return currentState.value === state;
  };

  return {
    // Raw XState refs
    snapshot,
    state: snapshot, // Alias for snapshot - used by step components for machine.state.value.matches()
    send,
    actorRef,

    // Computed state
    currentState,
    context,
    isLoading,

    // Step management (replaces usePayrollWizard.js)
    stepRegistry,
    visibleSteps,
    currentStepComponent,
    currentStepIndex,
    getStepKeyFromState,

    // Period data
    period,
    isEditMode,
    periodFormData,

    // Rules
    rules,
    hasGrossRevenueRules,
    hasCommissionRules,

    // Calculations
    calculationSummary,
    calculationsComplete,
    paymentByUser,

    // Refunds
    pendingRefundsCount,

    // Confirmation
    confirmationResult,

    // Error
    error,

    // Navigation helpers
    canProceed,
    canGoBack,
    getNextLabel,
    getNextLabelForStep,
    getStepIndex,  // Legacy - use currentStepIndex instead
    isInState,
    isStopped,

    // Actions
    startNew,
    resumePeriod,
    updateForm,
    submitConfig,
    next,
    back,
    confirm,
    startCalculations,
    markCalculationsDone,
    cancel,
    reset,
    goToStep,
    updateRevenue,
    updateExpenses
  };
}
